import { isSensitiveKey } from '@client-shared/utils/raygun-sensitive-keys.js'

// raygun4js copies URLs into breadcrumbs verbatim: every XHR becomes `'Opening request to ' + url`
// with the URL repeated in the crumb's metadata, and every history change records
// `location.pathname + location.search` plus the serialized router state. setFilterScope('all') does
// run filterSensitiveData over the breadcrumbs, but it matches KEYS — `requestURL`, `from` and `to`
// are not sensitive key names, so a credential riding INSIDE the URL string survives it. Nor does
// logContentsOfXhrCalls help: that flag gates the request/response BODY, never the URL.
//
// Two credentials reach raygun that way: `?auth_token_restricted=<jwt>` (download.api.js builds it
// into config.url) and the account confirmation token, which travels as `?confirmationtoken=<uuid>`
// in the confirm page URL (navigation crumbs) and as a bare path segment in
// `userregistrations/validate/<email>/<uuid>` (request crumbs).
//
// This runs from the onBeforeSend hook in each app's raygun.js plugin, i.e. on raygun's own clone of
// the payload — the app's breadcrumb trail itself is never modified.

const REDACTED = '[removed by filter]'

// `key=value` anywhere in a string. Deliberately not URL-parsed: the same pairs appear inside the
// JSON-serialized history state, where there is no URL to parse. The value stops at the delimiters
// that can end it in either context (`&`, quotes, whitespace), so only the value is replaced.
const KEY_VALUE_PAIR = /([\w.-]+)=([^&"'\s]*)/g

// The confirmation token is a crypto.randomUUID() and also appears as a bare path segment, where no
// key names it. It is the only UUID the API layer puts in a URL (entity ids are ObjectIds and stay
// readable), so redacting UUIDs wholesale costs no diagnostic value.
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi

export const redactCredentialsInText = (text) =>
  text
    .replace(KEY_VALUE_PAIR, (pair, key) => (isSensitiveKey(key) ? `${key}=${REDACTED}` : pair))
    .replace(UUID, REDACTED)

// Breadcrumb metadata is flat today (requestURL, from, to, prevState, …), but recursing keeps this
// independent of what raygun records next.
const redactValue = (value) => {
  if (typeof value === 'string') {
    return redactCredentialsInText(value)
  }

  if (Array.isArray(value)) {
    return value.map(redactValue)
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([
          key,
          val,
        ]) => [
          key,
          redactValue(val),
        ]),
    )
  }

  return value
}

export default (payload) => {
  const breadcrumbs = payload?.Details?.Breadcrumbs

  if (!Array.isArray(breadcrumbs)) {
    return payload
  }

  for (const crumb of breadcrumbs) {
    if (typeof crumb?.message === 'string') {
      crumb.message = redactCredentialsInText(crumb.message)
    }

    // raygun renames the crumb's `metadata` to `CustomData` while building the payload
    if (crumb?.CustomData) {
      crumb.CustomData = redactValue(crumb.CustomData)
    }
  }

  return payload
}
